import type { OpacityModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import OpacityIcon from "@svg/modifiers/opacity-icon";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { OPACITY_OPTIONS } from "@v2/features/modifiers/constants";
import { ChevronDown } from "lucide-react";
import { parseUnit } from "replo-runtime/shared/utils/units";

interface OpacityModifierProps {
  onChange: (modifier: OpacityModifierValue) => void;
}

const OpacityModifier: React.FC<OpacityModifierProps> = ({ onChange }) => {
  const currentValue = useSemanticModifierValue({
    semanticExtractor: ({ computedStyles }) => {
      const opacity = computedStyles.opacity;
      if (!opacity) {
        return null;
      }
      const num = Number.parseFloat(opacity);
      return isNaN(num) ? null : num;
    },
  });

  const mapDecimalToPercentage = (decimal: number): number => {
    return Math.round(decimal * 100);
  };

  const displayValue =
    currentValue != null ? `${mapDecimalToPercentage(currentValue)}%` : "100%";

  const handleValueChange = (opacity: number) => {
    onChange({
      type: "opacity",
      value: opacity,
    });
  };

  return (
    <div className="opacity-modifier">
      <LengthInputSelector
        field="opacity"
        value={displayValue}
        startEnhancer={<OpacityIcon className="w-3 h-3" />}
        onChange={(newValue) => {
          const result = parseUnit(
            newValue,
            { value: 100, unit: "%" },
            "default",
            "%",
          );
          const numericValue =
            typeof result.value === "number"
              ? result.value
              : Number.parseFloat(result.value);
          if (
            !Number.isNaN(numericValue) &&
            numericValue >= 0 &&
            numericValue <= 100
          ) {
            handleValueChange(numericValue / 100);
          }
        }}
        placeholder="100%"
        metrics={["%"]}
        unitDefaults={{ value: "100", unit: "%" }}
        menuOptions={OPACITY_OPTIONS}
        minValues={{ "%": 0 }}
        maxValues={{ "%": 100 }}
        inputClassName="w-full"
        endEnhancer={() => <ChevronDown size={12} />}
      />
    </div>
  );
};

export default OpacityModifier;
